
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import RequestPromises from "@/mixins/requestPromises";
import AdminBrandId from "@/mixins/adminBrandId";
import {
  IImport,
  IImportProviderData,
  IImportProviderMethods,
  IImportStep
} from "@/models/imports";
import _ from "@/boot/lodash";
import { ImportStatusCodes as ISC } from "@/data/enums/imports";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      importObj(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.IMPORTS,
          scope: this.dataScope
        });
      }
    })
  }
})
export default class ImportProvider extends Mixins(
  RequestPromises,
  AdminBrandId
) {
  // Props

  @Prop({ required: true })
  public readonly importId!: IImport["id"];

  // Data

  public readonly dataScope = `$import_${this.importId}`;
  public readonly importObj!: IImport;
  public pollInterval: NodeJS.Timeout | null = null;
  public activeStep = -1;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;

  // Life-cycle Methods

  public render(createElement: CreateElement) {
    if (this.isLoading)
      return createElement("is-loading", {
        props: { if: true },
        attrs: { isAbsolute: true }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $importData: this.importProvider(),
        $importMethods: this.importMethods
      })
    );
  }

  public created() {
    this.isLoading = true;
    this.getImport();
  }

  public beforeDestroy() {
    this.clearPoll();
    this.$store.dispatch("data/binSingle", {
      scope: this.dataScope,
      storeModule: DataModules.IMPORTS,
      vm: this
    });
  }

  // Provide

  @Provide("importProviderData")
  public importProvider(): IImportProviderData {
    return {
      activeStep: this.activeStep,
      canCancelImport: this.canCancelImport,
      canCommit: this.canCommit,
      canDelete: this.canDelete,
      canSkipStep: this.canSkipStep,
      canStartImport: this.canStartImport,
      import: this.importObj,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      steps: _.get(this.importObj, "steps", [])
    };
  }

  @Provide("importProviderMethods")
  public get importMethods(): IImportProviderMethods {
    return {
      cancel: this.cancel,
      commit: this.commit,
      delete: this.delete,
      openStep: this.openStep,
      retryStep: this.retryStep,
      refreshData: this.refreshData,
      skipStep: this.skipStep,
      start: this.start
    };
  }

  // Computed

  public get isInProgress() {
    return _.get(this.importObj, "status.code") === ISC.IN_PROGRESS;
  }

  public get isPollable() {
    return [ISC.EXPORTING, ISC.IN_PROGRESS].includes(
      _.get(this.importObj, "status.code")
    );
  }

  public get canStartImport() {
    return _.get(this.importObj, "status.code") === ISC.PENDING;
  }

  public get canCancelImport() {
    return [
      ISC.EXPORTING,
      ISC.MAPPINGS_REQUIRED,
      ISC.IN_PROGRESS,
      ISC.STAGED
    ].includes(_.get(this.importObj, "status.code"));
  }

  public get canDelete() {
    return [ISC.CANCELLED].includes(_.get(this.importObj, "status.code"));
  }

  public get canCommit() {
    return [ISC.STAGED].includes(_.get(this.importObj, "status.code"));
  }

  public get canSkipStep() {
    return this.$userCan("skip_import_step");
  }

  // Methods

  public async getImport(ignoreStored = false): Promise<void> {
    try {
      const { brand_id } = await this.$store.dispatch(
        `data/${DataModules.IMPORTS}/get`,
        {
          importId: this.importId,
          payload: {
            vm: this,
            scope: this.dataScope,
            ignoreStored
          }
        }
      );
      if (brand_id !== this.brandId) this.handle404NotFound(true);
      this.pollForUpdates();
    } catch (error) {
      this.clearPoll();
      this.$store.dispatch("api/handleError", error);
      if (this.$store.getters["api/is404"](error))
        await this.handle404NotFound();
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public handle404NotFound(replace = false) {
    return this.$router[replace ? "replace" : "push"]({
      name: "adminImports"
    });
  }

  public refreshData() {
    this.isReloading = true;
    return this.getImport(true);
  }

  public pollForUpdates(seconds = 5) {
    if (!this.isPollable) return this.clearPoll();
    if (this.pollInterval) return;
    this.pollInterval = setInterval(this.refreshData, seconds * 1000);
  }

  public clearPoll() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  public openStep(i = -1) {
    this.$set(this, "activeStep", i);
  }

  public async start() {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.IMPORTS}/start`, {
        importId: this.importId,
        payload: {
          vm: this
        }
      });
      await this.refreshData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public async retryStep(stepId: IImportStep["id"]) {
    if (this.isProcessing) return;
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.IMPORTS}/retryStep`, {
        importId: this.importId,
        stepId
      });
      await this.refreshData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public async skipStep(stepId: IImportStep["id"]) {
    if (this.isProcessing) return;
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.IMPORTS}/skipStep`, {
        importId: this.importId,
        stepId
      });
      await this.refreshData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public async cancel() {
    try {
      this.isProcessing = true;
      const cancelled: boolean = await this.$store.dispatch(
        `data/${DataModules.IMPORTS}/confirmCancel`,
        _.pick(this, ["importId"])
      );
      if (cancelled) {
        this.openStep();
        this.refreshData();
      }
    } finally {
      this.isProcessing = false;
    }
  }

  public async delete(): Promise<void> {
    try {
      this.isProcessing = true;
      const deleted: boolean = await this.$store.dispatch(
        `data/${DataModules.IMPORTS}/confirmDelete`,
        _.pick(this, ["importId"])
      );
      if (deleted) this.handle404NotFound();
    } finally {
      this.isProcessing = false;
    }
  }

  public async commit(): Promise<void> {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(
        `data/${DataModules.IMPORTS}/confirmCommit`,
        _.pick(this, ["importId"])
      );
    } finally {
      this.isProcessing = false;
    }
  }
}
